import React, { useEffect, useMemo, useState } from 'react';
import { Divider } from '@mui/material';
import get from 'lodash/get';
import sortBy from 'lodash/sortBy';
import PropTypes from 'prop-types';

import { usePutOrganization } from 'api';

import ToastMessage from 'components/toastMessage';
import { Box, Button, Switch } from 'components/ui';
import { DeleteConfirmationModal, EmailFormModal, ListItem } from './components';

import * as Styled from './SettingsDocumentAutosending.styles';

export const SettingsDocumentAutosending = ({ organizationData, refetchOrganizationData }) => {
  const [editEmailData, setEditEmailData] = useState(null);
  const [deleteEmailData, setDeleteEmailData] = useState(null);
  const [isAutomaticSendingEnabled, setIsAutomaticSendingEnabled] = useState(false);
  const [toastData, setToastData] = useState(null);

  useEffect(() => {
    setIsAutomaticSendingEnabled(get(organizationData, 'organization.automaticSendingOfDocuments.enabled', false));
  }, [organizationData]);

  const { mutate: updateOrganization, isLoading: isUpdatingOrganizationData } = usePutOrganization({
    onSuccess: () => {
      refetchOrganizationData();
      setEditEmailData(null);
      setDeleteEmailData(null);
      setToastData({ severity: 'success', message: 'Zapisano ustawienia' });
    },
    onError: () => {
      setToastData({ severity: 'error', message: 'Nie udało się zapisać ustawień' });
    },
  });

  const emails = useMemo(() => {
    return get(organizationData, 'organization.automaticSendingOfDocuments.emails', []);
  }, [organizationData]);

  const enabled = useMemo(() => {
    return get(organizationData, 'organization.automaticSendingOfDocuments.enabled', false);
  }, [organizationData]);

  const updateEmails = (oldEmailData, newEmailData) => {
    const newEmails = sortBy(
      [
        ...emails.filter((email) => email.email !== oldEmailData.email && email.email !== newEmailData.email),
        newEmailData,
      ],
      ['name'],
    );

    updateOrganization({
      automaticSendingOfDocuments: {
        emails: newEmails,
        enabled,
      },
    });
  };

  const updateEnabled = (value) => {
    setIsAutomaticSendingEnabled(value);
    updateOrganization({
      automaticSendingOfDocuments: {
        emails,
        enabled: value,
      },
    });
  };

  const deleteEmail = (emailData) => {
    updateOrganization({
      automaticSendingOfDocuments: {
        emails: emails.filter((email) => email.email !== emailData.email),
        enabled,
      },
    });
  };

  return (
    <Box title="Wysyłka dokumentów">
      <Button onClick={() => setEditEmailData({})}>Dodaj adres e-mail</Button>

      <Styled.List>
        {emails.map((email) => (
          <ListItem
            key={email.email}
            item={email}
            onEdit={() => setEditEmailData(email)}
            onDelete={() => setDeleteEmailData(email)}
          />
        ))}
      </Styled.List>

      <Divider />

      <Styled.H3>Ustawienia</Styled.H3>
      <Switch
        checked={isAutomaticSendingEnabled}
        onChange={updateEnabled}
        disabled={isUpdatingOrganizationData}
        label="Wysyłaj automatycznie"
      />

      {toastData && (
        <ToastMessage
          open
          onClose={() => setToastData(null)}
          severity={toastData.severity}
          message={toastData.message}
        />
      )}

      {editEmailData && (
        <EmailFormModal
          data={editEmailData}
          onClose={() => setEditEmailData(null)}
          onConfirm={(data) => updateEmails(editEmailData, data)}
          onDelete={() => setDeleteEmailData(editEmailData)}
          isSaving={isUpdatingOrganizationData}
        />
      )}

      {deleteEmailData && (
        <DeleteConfirmationModal
          onClose={() => setDeleteEmailData(null)}
          onConfirm={() => deleteEmail(deleteEmailData)}
          isSaving={isUpdatingOrganizationData}
        />
      )}
    </Box>
  );
};

SettingsDocumentAutosending.propTypes = {
  organizationData: PropTypes.object.isRequired,
  refetchOrganizationData: PropTypes.func.isRequired,
};
